import React, { useEffect, useState } from 'react';
import { clinicService } from '../../services/api';

const ClinicQuotaSection = ({ clinicId }) => {
  const [summary, setSummary] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ delta_quota: '', reason: '', request_id: '' });

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [quotaSummary, quotaAdjustments, quotaPurchases] = await Promise.all([
        clinicService.getClinicQuotaSummary(clinicId),
        clinicService.getClinicQuotaAdjustments(clinicId),
        clinicService.getClinicQuotaPurchases(clinicId),
      ]);
      setSummary(quotaSummary);
      setAdjustments(quotaAdjustments || []);
      setPurchases(quotaPurchases || []);
    } catch (err) {
      console.error('Error loading clinic quota data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load quota data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [clinicId]);

  const handleAdjustQuota = async (e) => {
    e.preventDefault();
    const delta = Number(form.delta_quota);
    if (!Number.isInteger(delta) || delta === 0) {
      setError('delta_quota must be a non-zero integer');
      return;
    }
    if (!form.reason.trim()) {
      setError('Reason is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await clinicService.adjustClinicQuota(clinicId, {
        delta_quota: delta,
        reason: form.reason.trim(),
        request_id: form.request_id.trim() || undefined,
      });
      setSuccess('Quota adjustment saved');
      setForm({ delta_quota: '', reason: '', request_id: '' });
      await loadData();
    } catch (err) {
      console.error('Error adjusting clinic quota:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to adjust quota');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-gray-500">Loading clinic quota...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-700">Clinic Quota</h3>
        <button
          onClick={loadData}
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
        >
          Refresh
        </button>
      </div>

      {error && <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      {success && <div className="p-3 bg-green-100 text-green-700 rounded">{success}</div>}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="p-3 bg-white border rounded"><div className="text-xs text-gray-500">基本配額 (每月)</div><div className="text-xl font-semibold">{summary.base_quota}</div></div>
          <div className="p-3 bg-white border rounded"><div className="text-xs text-gray-500">已購買</div><div className="text-xl font-semibold">{summary.purchased_quota}</div></div>
          <div className="p-3 bg-white border rounded"><div className="text-xs text-gray-500">已調整 (本月)</div><div className="text-xl font-semibold">{summary.adjusted_quota}</div></div>
          <div className="p-3 bg-white border rounded"><div className="text-xs text-gray-500">已使用 (本月)</div><div className="text-xl font-semibold">{summary.used}</div></div>
          <div className="p-3 bg-white border rounded"><div className="text-xs text-gray-500">總配額</div><div className="text-xl font-semibold">{summary.total_quota}</div></div>
        </div>
      )}

      <div className="bg-white border rounded p-4">
        <h4 className="font-medium text-gray-800 mb-3">Adjust Quota</h4>
        <form className="grid grid-cols-1 md:grid-cols-4 gap-3" onSubmit={handleAdjustQuota}>
          <input
            type="number"
            value={form.delta_quota}
            onChange={(e) => setForm((prev) => ({ ...prev, delta_quota: e.target.value }))}
            placeholder="Delta quota (e.g. 50, -10)"
            className="p-2 border rounded"
          />
          <input
            type="text"
            value={form.reason}
            onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
            placeholder="Reason"
            className="p-2 border rounded md:col-span-2"
          />
          <input
            type="text"
            value={form.request_id}
            onChange={(e) => setForm((prev) => ({ ...prev, request_id: e.target.value }))}
            placeholder="Request ID (optional)"
            className="p-2 border rounded"
          />
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 md:col-span-4 md:w-48"
          >
            {saving ? 'Saving...' : 'Apply Adjustment'}
          </button>
        </form>
      </div>

      <div className="bg-white border rounded p-4">
        <h4 className="font-medium text-gray-800 mb-3">Adjustment History</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full border">
            <thead><tr className="bg-gray-50"><th className="px-3 py-2 border text-left">Created At</th><th className="px-3 py-2 border text-left">Delta</th><th className="px-3 py-2 border text-left">Reason</th><th className="px-3 py-2 border text-left">Created By</th></tr></thead>
            <tbody>
              {adjustments.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2 border">{new Date(item.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 border">{item.delta_quota}</td>
                  <td className="px-3 py-2 border">{item.reason}</td>
                  <td className="px-3 py-2 border">{item.created_by || '-'}</td>
                </tr>
              ))}
              {adjustments.length === 0 && <tr><td className="px-3 py-3 border text-center text-gray-500" colSpan={4}>No adjustments</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border rounded p-4">
        <h4 className="font-medium text-gray-800 mb-3">Purchase History</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full border">
            <thead><tr className="bg-gray-50"><th className="px-3 py-2 border text-left">Created At</th><th className="px-3 py-2 border text-left">Package</th><th className="px-3 py-2 border text-left">Units</th><th className="px-3 py-2 border text-left">Status</th></tr></thead>
            <tbody>
              {purchases.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2 border">{new Date(item.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 border">{item.package_name}</td>
                  <td className="px-3 py-2 border">{item.quota_units}</td>
                  <td className="px-3 py-2 border">{item.status}</td>
                </tr>
              ))}
              {purchases.length === 0 && <tr><td className="px-3 py-3 border text-center text-gray-500" colSpan={4}>No purchases</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClinicQuotaSection;
